<script setup>
import { formatTelLink } from '@/composables/useFormatTelLink';
import { useBlockSafe } from '@/composables/useBlockSafe';
import ContactsMap from '@/components/Maps/ContactsMap.vue';
const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
});
const {
  id,
  backgroundColor,
  spacingTop,
  spacingBottom,
  phone,
  email,
  address,
  hasPhone,
  hasEmail,
  hasAddress,
  hasAnyContent,
} = useBlockSafe(props.data);
</script>
<template>
  <section
    v-if="hasAnyContent"
    :id="id"
    class="section"
    :class="[
      backgroundColor ? `bg-${backgroundColor}` : '',
      spacingTop ? `section--top-${spacingTop}` : 'section--top-xl',
      spacingBottom ? `section--bottom-${spacingBottom}` : 'section--bottom-xl',
    ]"
    data-aos="fade-up"
    data-aos-easing="ease-out-cubic"
  >
    <div class="container">
      <div :class="`contacts contacts--${data?.background_color}`">
        <div class="contacts__items">
          <div
            v-if="hasAddress"
            class="contacts__item h5"
            data-aos="fade-up"
            :data-aos-delay="100"
          >
            <CIcon
              class="contacts__item-icon"
              name="location-red"
              width="24"
              height="24"
            />
            {{ address }}
          </div>
          <div
            v-if="hasPhone"
            class="contacts__item"
            data-aos="fade-up"
            :data-aos-delay="200"
          >
            <CIcon
              class="contacts__item-icon"
              name="phone-red"
              width="24"
              height="24"
            />
            <template v-for="(ph, idx) in phone" :key="idx">
              <a
                class="h5 contacts__item-link"
                :href="`tel:${formatTelLink(ph)}`"
              >
                {{ ph }}
              </a>
            </template>
            <div v-if="data.time" class="body-1-regular">{{ data.time }}</div>
          </div>
          <div
            v-if="hasEmail"
            class="contacts__item h5"
            data-aos="fade-up"
            :data-aos-delay="300"
          >
            <CIcon
              class="contacts__item-icon"
              name="mail-red"
              width="24"
              height="24"
            />
            <template v-for="(mail, mailIndex) in email" :key="mailIndex">
              <a class="contacts__item-link" :href="`mailto:${mail}`">
                {{ mail }}
              </a>
            </template>
          </div>
        </div>
        <ContactsMap
          :marker="data.marker"
          data-aos="fade-up"
          data-aos-easing="ease-out-cubic"
        />
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
.contacts {
  $parent: &;
  display: flex;
  flex-direction: column;
  &__items {
    border-radius: 8px 8px 0 0;
    display: grid;
    grid-template-columns: 1fr;
    gap: 24px;
    padding: 24px;
    @include media-breakpoint-up(sm) {
      gap: 40px;
      padding: 32px 40px;
    }
    @include media-breakpoint-up(md) {
      gap: 0;
      grid-template-columns: 4.8fr 3.8fr 3.4fr;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(padding-bottom, 32);
      @include responsive-spacing(padding-top, 32);
      @include responsive-spacing(padding-left, 40);
      @include responsive-spacing(padding-right, 40);
    }
  }
  &__item {
    border-top: 1px solid $divider;
    display: flex;
    flex-direction: column;
    padding-top: 24px;
    row-gap: 16px;
    &:first-child {
      border: unset;
      padding-top: 0px;
    }
    @include media-breakpoint-up(sm) {
      padding-top: 40px;
    }
    @include media-breakpoint-up(md) {
      border-top: unset;
      border-left: 1px solid $divider;
      padding: 0 40px;
      &:first-child {
        border: unset;
        padding: 0 40px 0 0;
      }
      &:last-child {
        padding: 0 0 0 40px;
      }
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(border-left-width, 1);
      @include responsive-spacing(row-gap, 16);
      @include responsive-spacing(padding-left, 40);
      @include responsive-spacing(padding-right, 40);
      &:last-child {
        padding-right: 0;
        @include responsive-spacing(padding-left, 40);
      }
    }
    &-icon {
      height: 24px;
      width: 24px;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(height, 24);
        @include responsive-spacing(width, 24);
      }
    }
    &-link {
      word-break: break-all;
    }
  }
  &--white {
    #{$parent} {
      &__items {
        background-color: $gray;
      }
    }
  }
  &--gray {
    #{$parent} {
      &__items {
        background-color: $white;
      }
    }
  }
  &--black {
    #{$parent} {
      &__items {
        background-color: rgba($surface-container-opacity, 0.5);
      }
    }
  }
}
</style>
